<template>
  <q-page padding @keyup.enter="uploadCsv">
    <q-toolbar class="column">
      <q-toolbar-title class="text-5 q-mr-auto q-ma-sm">{{ $t('adjust_commissions') }}</q-toolbar-title>
      <q-breadcrumbs class="q-mr-auto q-ma-sm">
         <q-breadcrumbs-el :label="$t('dashboard')" to="/ " />
        <q-breadcrumbs-el :label="$t('manage')" to="/campaigns" />
        <q-breadcrumbs-el :label="$t('adjust_commissions')" exact />
      </q-breadcrumbs>
    </q-toolbar>
    <q-separator spaced />
    <q-card flat bordered>
      <q-card-section class="row q-col-gutter-md">
        <div class="col-6">
          <select-request
            :v-model="campaignSelect"
            outlined
            :endpoint="`redirect?app=ADVERTISER&path=/api/get/campaign`"
            option-label="name"
            option-value="id"
            :label="$t('select_campaign')"
            clearable
            formatLabelCampaign
            @update="onCampaignUpdate"
          />
        </div>
        <div class="col-6">
          <q-file outlined v-model="fileInput" @input="changeCSV" :label="$t('csv_file')">
            <template v-slot:prepend>
              <q-icon name="attach_file" />
            </template>
          </q-file>
        </div>
        <div class="col-12">
          <q-banner rounded class="bg-warning text-white q-pa-md">
            <div class="text-h6">{{ $t('attention_to_file_format') }}</div>
             {{ $t('commission_adjustment_file_instructions') }}
            <a href="#" @click="directToFile()">{{ $t('click_here') }}</a>
          </q-banner>
        </div>
        <div class="col-12" v-if="validationErrors.length > 0">
          <q-banner rounded class="bg-negative text-white q-pa-md">
            <div class="text-h6 q-mb-sm">{{ $t('commission_adjustment_validation_title') }}</div>
            <div class="text-body2 q-mb-sm">{{ $t('commission_adjustment_validation_subtitle') }}</div>
            <ul class="q-ma-none q-pl-md">
              <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
            </ul>
          </q-banner>
        </div>
        <div class="col-6"></div>
      </q-card-section>
    </q-card>

    <q-card flat bordered v-if="data.length > 0" class="q-mt-md">
      <q-card-actions vertical align="center">
        <q-btn
          unelevated
          class="full-width"
          :label="$t('adjust')"
          color="positive"
          no-caps
          style="max-width: 500px"
          :disable="!canUpload"
          :loading="checkingEligibility"
          @click="uploadCsv"
        />
      </q-card-actions>
      <q-card-section>
        <q-table flat :data="data" hide-pagination :pagination="paginationCustom" :columns="columns" />
      </q-card-section>
    </q-card>
  </q-page>
</template>

<script>
import SelectRequest from '../../widgets/inputsGlobal/SelectRequest';

export default {
  name: 'CommissionAdjustments',
  components: { SelectRequest },
  data() {
    return {
      paginationCustom: { rowsPerPage: 99999999999 },
      fileInput: null,
      campaignSelect: null,
      data: [],
      columns: [],
      validationErrors: [],
      formatErrors: [],
      transactionIds: [],
      checkingEligibility: false
    };
  },
  computed: {
    canUpload() {
      return (
        this.campaignSelect !== null &&
        this.fileInput !== null &&
        this.data.length > 0 &&
        this.validationErrors.length === 0 &&
        !this.checkingEligibility
      );
    }
  },
  methods: {
    directToFile() {
      window.open('https://afilio-v3.s3.amazonaws.com/Template_Ajuste_Comissao.csv', '_blank');
    },
    uuidV4() {
      return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
      );
    },
    onCampaignUpdate(value) {
      this.campaignSelect = value === null ? null : value.id;
      this.refreshEligibilityValidation();
    },
    isEmptyLine(columns) {
      if (!Array.isArray(columns) || columns.length === 0) {
        return true;
      }

      return columns.every((column) => String(column ?? '').trim() === '');
    },
    isValidConversionId(value) {
      const id = String(value ?? '').trim();
      return /^\d+$/.test(id) && Number(id) > 0;
    },
    isValidCommissionAmount(value) {
      if (value === null || value === undefined) {
        return false;
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return true;
      }

      const normalized = String(value)
        .trim()
        .replace(/R\$/g, '')
        .replace(/\s/g, '');

      if (normalized === '') {
        return false;
      }

      // BR com milhar: 1.234.567,89
      if (/^-?\d{1,3}(\.\d{3})+,\d+$/.test(normalized)) {
        return true;
      }

      // BR sem milhar: 1234,89
      if (/^-?\d+,\d+$/.test(normalized)) {
        return true;
      }

      // Americano / simples: 1234.89 ou 1234
      if (/^-?\d+(\.\d+)?$/.test(normalized)) {
        return true;
      }

      return false;
    },
    extractTransactionIds(rows) {
      const ids = [];

      rows.forEach((columns) => {
        if (this.isEmptyLine(columns) || !Array.isArray(columns) || columns.length < 1) {
          return;
        }

        const idRaw = String(columns[0] ?? '').trim();
        if (this.isValidConversionId(idRaw)) {
          ids.push(Number(idRaw));
        }
      });

      return [...new Set(ids)];
    },
    validateCsvRows(rows) {
      const errors = [];

      rows.forEach((columns, index) => {
        const lineNumber = index + 2; // header + índice 0-based

        if (this.isEmptyLine(columns)) {
          return;
        }

        if (!Array.isArray(columns) || columns.length < 4) {
          errors.push(
            this.$t('commission_adjustment_invalid_columns')
              .replace('{line}', lineNumber)
              .replace('{count}', Array.isArray(columns) ? columns.length : 0)
          );
          return;
        }

        const idRaw = String(columns[0] ?? '').trim();
        const valorConversaoRaw = String(columns[1] ?? '').trim();
        const comissaoAfilioRaw = String(columns[2] ?? '').trim();
        const comissaoAfiliadoRaw = String(columns[3] ?? '').trim();

        if (!this.isValidConversionId(idRaw)) {
          errors.push(
            this.$t('commission_adjustment_invalid_id')
              .replace('{line}', lineNumber)
              .replace('{value}', columns[0] ?? '')
          );
        }

        if (!this.isValidCommissionAmount(valorConversaoRaw)) {
          errors.push(
            this.$t('commission_adjustment_invalid_conversion_value')
              .replace('{line}', lineNumber)
              .replace('{value}', columns[1] ?? '')
          );
        }

        if (!this.isValidCommissionAmount(comissaoAfilioRaw)) {
          errors.push(
            this.$t('commission_adjustment_invalid_afilio_commission')
              .replace('{line}', lineNumber)
              .replace('{value}', columns[2] ?? '')
          );
        }

        if (!this.isValidCommissionAmount(comissaoAfiliadoRaw)) {
          errors.push(
            this.$t('commission_adjustment_invalid_affiliate_commission')
              .replace('{line}', lineNumber)
              .replace('{value}', columns[3] ?? '')
          );
        }
      });

      const hasDataRows = rows.some((columns) => !this.isEmptyLine(columns));
      if (!hasDataRows) {
        errors.push(this.$t('commission_adjustment_empty_file'));
      }

      return errors;
    },
    financialStateLabel(financialStateId) {
      const map = {
        1: this.$t('commission_adjustment_status_pending'),
        2: this.$t('commission_adjustment_status_liberated'),
        3: this.$t('commission_adjustment_status_invoiced'),
        4: this.$t('commission_adjustment_status_paid')
      };

      return map[financialStateId] || `${this.$t('commission_adjustment_status_unknown')} (${financialStateId})`;
    },
    mapEligibilityErrors(invalidItems) {
      return (invalidItems || []).map((item) => {
        if (item.reason === 'financial_state_not_pending') {
          return this.$t('commission_adjustment_not_pending')
            .replace('{id}', item.transaction_id)
            .replace('{status}', this.financialStateLabel(item.financial_state_id));
        }

        if (item.reason === 'campaign_mismatch') {
          return this.$t('commission_adjustment_campaign_mismatch')
            .replace('{id}', item.transaction_id)
            .replace('{campaign}', item.campaign_id)
            .replace('{selected}', this.campaignSelect);
        }

        if (item.reason === 'conversion_not_found') {
          return this.$t('commission_adjustment_not_found').replace('{id}', item.transaction_id);
        }

        return item.message || this.$t('commission_adjustment_not_found').replace('{id}', item.transaction_id);
      });
    },
    async refreshEligibilityValidation(showDialog = false) {
      this.validationErrors = [...this.formatErrors];

      if (this.formatErrors.length > 0 || !this.campaignSelect || this.transactionIds.length === 0) {
        if (showDialog && this.validationErrors.length > 0) {
          this.showValidationErrorsDialog(this.validationErrors);
        }
        return;
      }

      try {
        this.checkingEligibility = true;

        const { data } = await this.$http.post('/v2/redirect', {
          app: 'CONVERSORPHP',
          path: '/api/commission-adjustment/validate',
          params: {
            data: {
              campaign_id: this.campaignSelect,
              transaction_ids: this.transactionIds
            }
          }
        });

        const eligibilityErrors = this.mapEligibilityErrors(data?.invalid || []);
        this.validationErrors = [...this.formatErrors, ...eligibilityErrors];

        if (showDialog && this.validationErrors.length > 0) {
          this.showValidationErrorsDialog(this.validationErrors);
        }
      } catch (error) {
        this.errorNotify(this.$t('commission_adjustment_eligibility_check_error'));
        if (error.response && error.response.status !== 500) {
          this.errorNotify(error);
        }
      } finally {
        this.checkingEligibility = false;
      }
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    showValidationErrorsDialog(errors) {
      const listItems = errors.map((error) => `<li>${this.escapeHtml(error)}</li>`).join('');
      this.showInfoDialog(
        this.$t('commission_adjustment_validation_title'),
        `<p>${this.escapeHtml(this.$t('commission_adjustment_validation_subtitle'))}</p><ul>${listItems}</ul>`
      );
    },
    async uploadCsv() {
      try {
        if (!this.campaignSelect) {
          this.errorNotify(this.$t('campaign_required'));
          return;
        }

        if (!this.fileInput) {
          this.errorNotify(this.$t('commission_adjustment_file_required'));
          return;
        }

        await this.refreshEligibilityValidation(true);

        if (this.validationErrors.length > 0) {
          return;
        }

        if (this.data.length === 0) {
          this.errorNotify(this.$t('commission_adjustment_empty_file'));
          return;
        }

        this.onLoading(true);

        let renameTo = `comission/conversor/adjustComission_${this.campaignSelect}_${
          this.user.user_id
        }_${this.$moment().format('YYYY_MM_DD')}_${this.$moment().format('H_I_S')}_${this.uuidV4()}.csv`;

        let form = new FormData();
        form.append('file', this.fileInput);
        form.append('file_rename', renameTo);
        form.append('bucket', 's3AdjustCommission');

        const { status } = await this.$http.post('/v2/upload/file', form);
        if (status === 200) {
          this.fileInput = null;
          this.campaignSelect = null;
          this.data = [];
          this.columns = [];
          this.validationErrors = [];
          this.formatErrors = [];
          this.transactionIds = [];

          this.showInfoDialog(
            this.$t('commission_adjustment_processing_message')
          );
        }
      } catch (error) {
        if (error.response && error.response.status === 500)
          this.errorNotify(this.$t('problem_try_again_later'));
        else this.errorNotify(error);
      } finally {
        this.onLoading(false);
      }
    },

    changeCSV(e) {
      this.data = [];
      this.columns = [];
      this.validationErrors = [];
      this.formatErrors = [];
      this.transactionIds = [];

      if (!e) {
        return;
      }

      let readCsv = new FileReader();
      readCsv.readAsText(e);

      readCsv.onload = async () => {
        let csvList = readCsv.result
          .split(/\r?\n/)
          .map((line) => line.trimEnd())
          .filter((line, index, list) => !(line === '' && index === list.length - 1));

        if (csvList.length === 0) {
          this.formatErrors = [this.$t('commission_adjustment_empty_file')];
          this.validationErrors = [...this.formatErrors];
          this.showValidationErrorsDialog(this.validationErrors);
          return;
        }

        let columns = csvList[0].split(';');

        let i = 0;
        columns.forEach((element) => {
          this.columns.push({
            name: element,
            label: element,
            field: element,
            align: 'left',
            index: i
          });
          i++;
        });

        let dataFormat = csvList.filter((item) => item != csvList[0]);
        const parsedRows = [];

        dataFormat.forEach((el) => {
          if (el !== '') {
            let element = el.split(';');
            parsedRows.push(element);

            let data = {};
            this.columns.forEach((itemColum) => {
              data[itemColum.label] = element[itemColum.index];
            });

            this.data.push(data);
          } else {
            parsedRows.push([]);
          }
        });

        this.formatErrors = this.validateCsvRows(parsedRows);
        this.transactionIds = this.extractTransactionIds(parsedRows);
        await this.refreshEligibilityValidation(true);
      };
    }
  }
};
</script>

<style></style>
